import React, { useState } from 'react';
import { Phone, PhoneCall, Copy, Settings, Brain, FileText, ChevronDown, ChevronUp, Loader2, Sparkles, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '@/components/ui/tooltip';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import api, { useAuth } from '@/lib/api';
import logger from '@/lib/logger';

// 3CX Web Client URL - this should match your 3CX server
const THREECX_WEB_CLIENT = 'https://clt-academy.3cx.ae:5001';

/**
 * Click-to-Call Button Component
 * Integrates with 3CX phone system for making calls
 * 
 * Usage:
 * <ClickToCall phoneNumber="+971501234567" contactId="uuid" contactName="John Doe" />
 */
export function ClickToCall({ 
    phoneNumber, 
    contactId = null, 
    contactName = null,
    variant = "ghost",
    size = "icon",
    showLabel = false,
    className = ""
}) {
    const [isDialing, setIsDialing] = useState(false);

    if (!phoneNumber) return null;

    // Clean phone number for 3CX
    const cleanNumber = phoneNumber.replace(/\s+/g, '').replace(/[()-]/g, '');

    const handleCallDirect = async () => {
        setIsDialing(true);
        
        try {
            // Call via 3CX Call Control API — rings agent's phone directly
            const res = await api.post('/3cx/click-to-call', null, {
                params: {
                    phone_number: phoneNumber,
                    contact_id: contactId
                }
            });
            const data = res.data;
            if (data.call_control) {
                toast.success(data.message || `Calling ${contactName || phoneNumber}...`, {
                    description: 'Your 3CX phone will ring — pick up to connect',
                    duration: 5000
                });
            } else {
                // API logged it but couldn't place via Call Control — use tel: fallback
                window.location.href = `tel:${cleanNumber}`;
                toast.info(`Dialing ${contactName || phoneNumber}...`, {
                    description: data.message || 'Connecting via phone',
                    duration: 4000
                });
            }
        } catch (error) {
            logger.error('Click-to-call error:', error);
            // Last resort fallback
            window.location.href = `tel:${cleanNumber}`;
            toast.info(`Dialing ${contactName || phoneNumber}...`);
        }
        
        setTimeout(() => setIsDialing(false), 3000);
    };

    const handleCopy = () => {
        navigator.clipboard.writeText(phoneNumber);
        toast.success('Phone number copied!');
    };

    return (
        <TooltipProvider>
            <DropdownMenu>
                <Tooltip>
                    <TooltipTrigger asChild>
                        <DropdownMenuTrigger asChild>
                            <Button
                                variant={variant}
                                size={size}
                                disabled={isDialing}
                                className={`${className} ${isDialing ? 'animate-pulse' : ''}`}
                                data-testid="click-to-call-btn"
                            >
                                {isDialing ? (
                                    <PhoneCall className="h-4 w-4 text-green-500 animate-bounce" />
                                ) : (
                                    <Phone className="h-4 w-4" />
                                )}
                                {showLabel && (
                                    <span className="ml-2">{isDialing ? 'Calling...' : 'Call'}</span>
                                )}
                            </Button>
                        </DropdownMenuTrigger>
                    </TooltipTrigger>
                    <TooltipContent side="top">
                        Click to call: {phoneNumber}
                    </TooltipContent>
                </Tooltip>
                
                <DropdownMenuContent align="end" className="w-56">
                    <DropdownMenuItem onClick={handleCallDirect} className="cursor-pointer">
                        <Phone className="mr-2 h-4 w-4 text-green-500" />
                        <span>Call via 3CX</span>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleCopy} className="cursor-pointer">
                        <Copy className="mr-2 h-4 w-4" />
                        <span>Copy number</span>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem className="text-xs text-muted-foreground" disabled>
                        <Settings className="mr-2 h-3 w-3" />
                        <span>{phoneNumber}</span>
                    </DropdownMenuItem>
                </DropdownMenuContent>
            </DropdownMenu>
        </TooltipProvider>
    );
}

/**
 * Call History Display Component (with NLP Transcript + AI Coaching)
 */
export function CallHistory({ contactId, className = "" }) {
    const { user } = useAuth();
    const [calls, setCalls] = React.useState([]);
    const [loading, setLoading] = React.useState(true);
    const [expandedCall, setExpandedCall] = React.useState(null);
    const [analyzingCall, setAnalyzingCall] = React.useState(null);
    const [syncing, setSyncing] = React.useState(false);

    const canTriggerAnalysis = user && ["super_admin", "admin", "cs_head", "team_leader", "mentor", "master_of_academics"].includes(user.role);

    const fetchCalls = async () => {
        try {
            const response = await api.get(`/3cx/call-history/${contactId}`);
            setCalls(response.data.calls || []);
        } catch (error) {
            logger.error('Error fetching call history:', error);
        } finally {
            setLoading(false);
        }
    };

    React.useEffect(() => {
        if (!contactId) return;
        fetchCalls();
    }, [contactId]);

    const handleForceSync = async () => {
        setSyncing(true);
        try {
            const res = await api.post('/3cx/force-sync-initiated', { hours: 48 });
            const d = res.data;
            if (d.resolved > 0 || d.cleaned > 0) {
                toast.success(`Synced ${d.resolved} calls, cleaned ${d.cleaned} stale entries`);
                fetchCalls(); // Refresh
            } else {
                toast.info(d.message || 'No calls to sync');
            }
        } catch (err) {
            toast.error('Sync failed — 3CX may be unreachable');
        }
        setSyncing(false);
    };

    const handleAnalyze = async (callId) => {
        setAnalyzingCall(callId);
        try {
            await api.post(`/nlp/analyze/${callId}`);
            toast.success('AI analysis started — results will appear shortly');
            // Poll for completion
            const pollInterval = setInterval(async () => {
                try {
                    const res = await api.get(`/nlp/transcript/${callId}`);
                    if (res.data.nlp_status === 'completed' || res.data.nlp_status === 'error') {
                        clearInterval(pollInterval);
                        setCalls(prev => prev.map(c => c.call_id === callId ? {
                            ...c,
                            nlp_status: res.data.nlp_status,
                            transcript: res.data.transcript,
                            ai_analysis: res.data.ai_analysis,
                            transcript_language: res.data.transcript_language,
                        } : c));
                        setAnalyzingCall(null);
                        if (res.data.nlp_status === 'completed') {
                            toast.success('AI analysis complete');
                            setExpandedCall(callId);
                        } else {
                            toast.error('Analysis failed: ' + (res.data.nlp_error || 'Unknown error'));
                        }
                    }
                } catch { /* keep polling */ }
            }, 4000);
            // Safety: stop after 3 min
            setTimeout(() => { clearInterval(pollInterval); setAnalyzingCall(null); }, 180000);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to start analysis');
            setAnalyzingCall(null);
        }
    };

    if (loading) {
        return <div className="text-sm text-muted-foreground">Loading calls...</div>;
    }

    if (calls.length === 0) {
        return <div className="text-sm text-muted-foreground">No call history</div>;
    }

    const formatDuration = (seconds) => {
        if (!seconds || seconds === 0) return '0:00';
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '';
        const date = new Date(dateStr);
        return date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const SentimentBadge = ({ score }) => {
        if (!score) return null;
        const color = score >= 7 ? 'bg-green-500/15 text-green-600 border-green-500/30'
            : score >= 4 ? 'bg-amber-500/15 text-amber-600 border-amber-500/30'
            : 'bg-red-500/15 text-red-600 border-red-500/30';
        const label = score >= 7 ? 'Positive' : score >= 4 ? 'Neutral' : 'Negative';
        return <span className={`text-[9px] font-medium px-1.5 py-0.5 rounded border ${color}`}>{label} {score}/10</span>;
    };

    const AgentScoreBadge = ({ score }) => {
        if (!score) return null;
        const color = score >= 7 ? 'bg-emerald-500/15 text-emerald-600 border-emerald-500/30'
            : score >= 4 ? 'bg-yellow-500/15 text-yellow-600 border-yellow-500/30'
            : 'bg-red-500/15 text-red-600 border-red-500/30';
        return <span className={`text-[9px] font-medium px-1.5 py-0.5 rounded border ${color}`}>Agent {score}/10</span>;
    };

    const hasStuckCalls = calls.some(c => c.call_duration === 0 || c.call_duration === null);

    return (
        <div className={`space-y-2 ${className}`} data-testid="call-history">
            <div className="flex items-center justify-between">
                <h4 className="text-sm font-medium">Recent Calls</h4>
                {hasStuckCalls && (
                    <Button variant="outline" size="sm" className="h-6 px-2 text-[10px] gap-1"
                        onClick={handleForceSync} disabled={syncing} data-testid="force-sync-btn">
                        <RefreshCw className={`h-3 w-3 ${syncing ? 'animate-spin' : ''}`} />
                        {syncing ? 'Syncing...' : 'Force Sync'}
                    </Button>
                )}
            </div>
            <div className="space-y-1.5 max-h-[400px] overflow-y-auto">
                {calls.slice(0, 10).map((call) => {
                    const isInitiated = call.call_duration === 0 || call.call_duration === null;
                    const hasNlp = call.nlp_status === 'completed' && call.ai_analysis;
                    const isProcessing = call.nlp_status === 'processing' || analyzingCall === call.call_id;
                    const isExpanded = expandedCall === call.call_id;
                    const hasRecording = !!call.recording_url;

                    return (
                        <div key={call.call_id} className="rounded-lg border overflow-hidden">
                            {/* Call row */}
                            <div
                                className={`flex items-center justify-between text-xs p-2 cursor-pointer transition-colors ${
                                    isInitiated ? 'bg-amber-500/10 border-amber-500/20' : hasNlp ? 'bg-purple-500/5' : 'bg-muted/50'
                                } hover:bg-muted/80`}
                                onClick={() => setExpandedCall(isExpanded ? null : call.call_id)}
                                data-testid={`call-row-${call.call_id}`}
                            >
                                <div className="flex items-center gap-2">
                                    {call.call_direction === 'Inbound' ? (
                                        <PhoneCall className="h-3 w-3 text-blue-500" />
                                    ) : (
                                        <Phone className={`h-3 w-3 ${isInitiated ? 'text-amber-500' : 'text-green-500'}`} />
                                    )}
                                    <span className={call.call_type === 'Missed' ? 'text-red-500' : ''}>
                                        {call.call_direction} {call.call_type === 'Missed' && '(Missed)'}
                                    </span>
                                    {isInitiated && <span className="text-[9px] text-amber-600 font-medium px-1 py-0.5 bg-amber-100 rounded">Dialed — awaiting 3CX sync</span>}
                                    {hasNlp && <SentimentBadge score={call.ai_analysis?.sentiment_score} />}
                                    {hasNlp && <AgentScoreBadge score={call.ai_analysis?.agent_score} />}
                                    {isProcessing && <span className="text-[9px] text-purple-600 font-medium px-1 py-0.5 bg-purple-100 rounded flex items-center gap-1"><Loader2 className="h-2.5 w-2.5 animate-spin" />Analyzing...</span>}
                                    {call.transcript_language && <span className="text-[9px] text-muted-foreground uppercase">{call.transcript_language}</span>}
                                </div>
                                <div className="flex items-center gap-3 text-muted-foreground">
                                    {!isInitiated && <span className="font-mono">{formatDuration(call.call_duration)}</span>}
                                    <span>{formatDate(call.call_date)}</span>
                                    {hasRecording && !hasNlp && !isProcessing && canTriggerAnalysis && (
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            className="h-5 px-1.5 text-[9px] text-purple-600 hover:text-purple-700 hover:bg-purple-100"
                                            onClick={(e) => { e.stopPropagation(); handleAnalyze(call.call_id); }}
                                            data-testid={`analyze-btn-${call.call_id}`}
                                        >
                                            <Brain className="h-3 w-3 mr-0.5" />
                                            Analyze
                                        </Button>
                                    )}
                                    {call.recording_url && (
                                        <a
                                            href={call.recording_url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-primary hover:underline text-[10px]"
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            Recording
                                        </a>
                                    )}
                                    {(hasNlp || hasRecording) && (
                                        isExpanded
                                            ? <ChevronUp className="h-3 w-3" />
                                            : <ChevronDown className="h-3 w-3" />
                                    )}
                                </div>
                            </div>

                            {/* Expanded NLP Panel */}
                            {isExpanded && hasNlp && (
                                <div className="p-3 bg-gradient-to-b from-purple-500/5 to-transparent border-t border-purple-500/10 space-y-3" data-testid={`nlp-panel-${call.call_id}`}>
                                    {/* AI Summary */}
                                    {call.ai_analysis?.summary && (
                                        <div className="flex gap-2">
                                            <Sparkles className="h-3.5 w-3.5 text-purple-500 mt-0.5 shrink-0" />
                                            <p className="text-xs text-foreground/80">{call.ai_analysis.summary}</p>
                                        </div>
                                    )}
                                    
                                    {/* Key Topics */}
                                    {call.ai_analysis?.key_topics?.length > 0 && (
                                        <div className="flex flex-wrap gap-1">
                                            {call.ai_analysis.key_topics.map((t, i) => (
                                                <Badge key={i} variant="outline" className="text-[9px] px-1.5 py-0">{t}</Badge>
                                            ))}
                                        </div>
                                    )}

                                    {/* Strengths & Improvements grid */}
                                    <div className="grid grid-cols-2 gap-2">
                                        {call.ai_analysis?.strengths?.length > 0 && (
                                            <div className="p-2 rounded bg-green-500/5 border border-green-500/15">
                                                <p className="text-[10px] font-semibold text-green-600 mb-1">Strengths</p>
                                                <ul className="text-[10px] text-foreground/70 space-y-0.5">
                                                    {call.ai_analysis.strengths.map((s, i) => <li key={i}>+ {s}</li>)}
                                                </ul>
                                            </div>
                                        )}
                                        {call.ai_analysis?.improvements?.length > 0 && (
                                            <div className="p-2 rounded bg-amber-500/5 border border-amber-500/15">
                                                <p className="text-[10px] font-semibold text-amber-600 mb-1">Coaching Tips</p>
                                                <ul className="text-[10px] text-foreground/70 space-y-0.5">
                                                    {call.ai_analysis.improvements.map((s, i) => <li key={i}>- {s}</li>)}
                                                </ul>
                                            </div>
                                        )}
                                    </div>

                                    {/* Transcript excerpt */}
                                    {call.transcript && (
                                        <TranscriptPreview transcript={call.transcript} callId={call.call_id} />
                                    )}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

function TranscriptPreview({ transcript, callId }) {
    const [showFull, setShowFull] = React.useState(false);
    const preview = transcript.length > 300 ? transcript.slice(0, 300) + '...' : transcript;

    return (
        <div className="border border-muted rounded p-2" data-testid={`transcript-${callId}`}>
            <div className="flex items-center justify-between mb-1">
                <span className="text-[10px] font-semibold text-muted-foreground flex items-center gap-1">
                    <FileText className="h-3 w-3" /> Transcript
                </span>
                {transcript.length > 300 && (
                    <button
                        className="text-[9px] text-primary hover:underline"
                        onClick={() => setShowFull(!showFull)}
                    >
                        {showFull ? 'Collapse' : 'Show Full'}
                    </button>
                )}
            </div>
            <p className="text-[10px] text-foreground/70 whitespace-pre-wrap leading-relaxed">
                {showFull ? transcript : preview}
            </p>
        </div>
    );
}

export default ClickToCall;
